import { Box, Button, CircularProgress } from '@mui/material';

/**
 * Shared "Apply" button for every dashboard filter bar. This is the single
 * source of truth for the Apply CTA — all dashboards reuse this component so
 * the color, height, radius, and font stay identical instead of each page
 * duplicating (and drifting on) the inline style.
 *
 * Visual style is the Diagnosis Apply button: grey-blue fill `#475569`
 * (hover `#334155`), white label, fixed 40px height, the theme's 10px radius
 * and Poppins/600 button font. When disabled it goes grey (`#CBD5E1`) with a
 * white label.
 *
 * Disabled-cursor behaviour: a disabled MUI Button sets `pointer-events: none`,
 * so the browser never shows a cursor over it. To still show the `not-allowed`
 * (🚫) cursor on hover when there is nothing to apply, the button is wrapped in
 * a `<span>` that owns the cursor; the span receives the hover even though the
 * button swallows pointer events for clicks.
 *
 * @param {Object} props
 * @param {() => void} props.onClick - Apply handler (required).
 * @param {boolean} [props.disabled=false] - Disable the button, e.g. when the
 *   current selection already equals the applied one (nothing to apply).
 * @param {boolean} [props.loading=false] - Show a spinner and disable while a
 *   request is in flight.
 * @param {boolean} [props.fullWidth=false] - Force full width (mobile/stacked
 *   filter bars). When false, the button is still full width below `sm` and
 *   auto width from `sm` up, matching the responsive filter bar.
 * @param {string} [props.label='Apply'] - Button text.
 */
export function FilterApplyButton({
  onClick,
  disabled = false,
  loading = false,
  fullWidth = false,
  label = 'Apply',
}) {
  const isDisabled = disabled || loading;
  const width = fullWidth ? '100%' : { xs: '100%', sm: 'auto' };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        width,
        // Only the disabled state shows the 🚫 cursor; the span owns it because
        // the disabled button below has pointer-events: none.
        cursor: isDisabled ? 'not-allowed' : 'default',
      }}
    >
      <Button
        variant="contained"
        onClick={onClick}
        disabled={isDisabled}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        sx={{
          height: 40,
          width: '100%',
          backgroundColor: '#475569',
          color: '#FFFFFF',
          '&:hover': { backgroundColor: '#334155' },
          '&.Mui-disabled': { backgroundColor: '#CBD5E1', color: '#FFFFFF' },
        }}
      >
        {label}
      </Button>
    </Box>
  );
}

export default FilterApplyButton;
